import {
  AI,
  CHAT_MODEL_TYPE,
  CHAT_STATUS,
  DOCUMENT_TYPE,
  PAYLOAD_LANGUAGE,
  POLLING_INTERVAL,
  ROUTE_NAMES,
  STRING,
  USER,
} from "@root/constants";
import { getTodayQuestions } from "@root/store/selectors/entities";
import { selectConGptFilesPopover } from "@root/store/selectors/entities/dashboard";
import {
  updateSelectedModel,
  updateThreadList,
  updateThreads,
} from "@root/store/slices/entities";
import { getThreads } from "@root/store/thunks";
import {
  doMultiAIChat,
  doQuery,
  doQueryDrawingAnalysis,
  doQueryKCS,
  doQueryMultiDoc,
  doQueryProject,
} from "@root/store/thunks/chatThunks";
import { generateRandomId } from "@root/utils/dateHelper";
import { WEB_PROJECTS } from "@root/webConstants";
import { ChangeEvent, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useSystem from "./useSystem";
import { getSelectedModel } from "@root/store/selectors/entities/thread";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useProjectsContext } from "@root/context/projects-context";
import { ChatService } from "@root/services";
import { getBaseUrl } from "@root/store/selectors/features";

const parseJSONField = (value: any, key: string): any[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === STRING) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed?.[key]) ? parsed[key] : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Answer/sources/follow-ups from a chat record (backend stores them as JSON strings). */
const parseChatRecord = (data: any) => {
  let answerText: string | undefined;
  if (typeof data?.response === STRING) {
    try {
      const parsed = JSON.parse(data.response);
      answerText = parsed?.answer ?? undefined;
    } catch {
      answerText = data.response;
    }
  } else if (typeof data?.answer === STRING) {
    answerText = data?.answer;
  }
  return {
    answerText,
    parsedSources: parseJSONField(data?.sources, "sources"),
    parsedFollowup: parseJSONField(data?.followUp, "followUp"),
  };
};

const MAX_POLL_FAILURES = 5;

const FINISHED_CHAT_STATUSES = [
  CHAT_STATUS?.COMPLETE,
  CHAT_STATUS?.EXCEPTION,
  CHAT_STATUS?.NO_CHANGE,
];

const useProjectsDashboard = () => {
  const defaultState = {
    riskCheckerInput: "",
    conGptInput: "",
    doazGptInput: "",
  };
  const modelOptions: any = JSON.parse(
    process.env.REACT_APP_CHAT_MODELES ?? "[]"
  );
  const { t } = useTranslation("common");
  const [state, setState] = useState(defaultState);
  const [selectedField, setSelectedField] = useState({ id: 1 });
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  // Multi-AI Chat state
  const [multiAIChatId, setMultiAIChatId] = useState<string | null>(null);
  const [multiAIThreadId, setMultiAIThreadId] = useState<string | null>(null);
  const [isMultiAIPolling, setIsMultiAIPolling] = useState(false);
  // Question sent while a reply was still generating; submitted once it completes
  const [queuedQuery, setQueuedQuery] = useState<{
    input: string;
    modelId?: number;
  } | null>(null);
  const [lastQuery, setLastQuery] = useState<string>("");
  const [lastType, setLastType] = useState<number>(0);
  const [multiAIMessages, setMultiAIMessages] = useState<any[]>([]);
  const [multiAISources, setMultiAISources] = useState<any[]>([]);
  const [currentAIResponseId, setCurrentAIResponseId] = useState<string | null>(
    null
  );
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [currentStatus, setCurrentStatus] = useState<number | null>(null);
  const [completedMessageIds, setCompletedMessageIds] = useState<Set<number>>(
    new Set()
  );
  // Consecutive failed polls; brief network blips shouldn't end a reply
  const pollFailuresRef = useRef(0);

  // Show an error as the AI reply (replacing any reply with the same id)
  const showChatError = (id: any, message: string) => {
    setMultiAIMessages((prev) => [
      ...prev.filter((msg: any) => !(msg?.type === AI && msg?.id === id)),
      {
        id,
        type: AI,
        content: message,
        timestamp: new Date().toISOString(),
        isError: true,
        skipTypewriter: true,
      },
    ]);
  };
  const conGptPopover = useSelector(selectConGptFilesPopover);
  const doazGptInputRef = useRef<any>(null);
  const conGptInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isOnline } = useSystem();
  // @ts-ignore
  const questions: any = useSelector(getTodayQuestions)?.rows || [];
  const riskCheckerQuestions = questions?.filter(
    (q: any) => q?.project_id === WEB_PROJECTS.RISK_CHECKER.id
  );
  const conGptQuestions = questions?.filter(
    (q: any) => q?.project_id === WEB_PROJECTS.CON_GPT.id
  );
  const doazGptQuestions = questions?.filter(
    (q: any) => q?.project_id === WEB_PROJECTS.DOAZ_GPT.id
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // From the router location: the context's currentPathname is "" on first render
  const multiAIChatType = pathname?.includes(ROUTE_NAMES.ASK_AI_CHATBOT)
    ? CHAT_MODEL_TYPE.ASK_AI
    : pathname?.includes(ROUTE_NAMES.LEGAL_COMPLIANCE)
    ? CHAT_MODEL_TYPE.LEGAL_COMPLIANCE
    : CHAT_MODEL_TYPE.MULTI_PROMPT;
  const selectedModel = useSelector(getSelectedModel);
  const baseUrl = useSelector(getBaseUrl);

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setState({ ...defaultState, [e.target.name]: e.target.value });
  };

  const currentLanguage = localStorage?.getItem("i18nextLng")?.toUpperCase();

  const validator = (question?: string) => {
    if (question && question.length > 0) {
      return true;
    }
    if (!isOnline) {
      return false;
    }
    if (!state.doazGptInput.length || state.doazGptInput.length === 0) {
      return false;
    }
    return true;
  };

  const {
    urls,
    urlType,
    setUrl,
    setQueryType,
    getInitialThreads,
    filesPreview,
    chatMode,
    setCurrentMessageResponse,
    currentPathname,
    docChatMode,
    selectedProject,
    getSource,
    getMode,
    getFilesPath,
    getDocNames,
    getPdfPageNum,
    setInput,
    setShowWelcomeText,
  } = useMainChatContext();

  const { handleProjectClick, projectsData, getFilename, documentData } =
    useProjectsContext();

  // Polling effect for Multi-AI Chat
  useEffect(() => {
    let pollingInterval: any;

    if (isMultiAIPolling && multiAIChatId && multiAIThreadId) {
      pollingInterval = setInterval(() => {
        const pollingPayload = {
          query: lastQuery,
          type: lastType,
          chatType: multiAIChatType,
          chatId: multiAIChatId,
          threadId: multiAIThreadId,
          isPolling: true,
          onSuccess: (data: any) => {
            console.log("Multi-AI Chat polling response:", data);
            pollFailuresRef.current = 0;

            // Track the current status
            setCurrentStatus(data?.status);

            // Handle AI response from polling
            try {
              const { answerText, parsedSources, parsedFollowup } =
                parseChatRecord(data);

              // Update AI message on every polling response, not just when complete
              if (answerText) {
                const serverMessageId = data?.id;
                if (serverMessageId) {
                  // Ensure we track the server-provided id
                  if (currentAIResponseId !== serverMessageId) {
                    setCurrentAIResponseId(serverMessageId);
                  }
                  const aiMessage = {
                    id: serverMessageId,
                    type: AI as any,
                    content: answerText,
                    timestamp: new Date()?.toISOString(),
                    sources: parsedSources,
                    followUp: parsedFollowup,
                  };
                  setMultiAIMessages((prev: any) => {
                    // Find the AI message with the server id
                    const existingAIMessageIndex = prev?.findIndex(
                      (msg: any) =>
                        msg?.id === serverMessageId && msg?.type === AI
                    );

                    if (existingAIMessageIndex !== -1) {
                      // Update existing AI message
                      const updatedMessages = [...prev];
                      updatedMessages[existingAIMessageIndex] = aiMessage;
                      return updatedMessages;
                    } else {
                      // Add new AI message
                      return [...prev, aiMessage];
                    }
                  });
                  if (parsedSources?.length > 0) {
                    setMultiAISources(parsedSources);
                  }
                }
              }
            } catch {}

            // Check if response is complete
            if (
              data?.status === CHAT_STATUS?.COMPLETE ||
              data?.status === CHAT_STATUS?.EXCEPTION ||
              data?.status === CHAT_STATUS?.NO_CHANGE
            ) {
              // Mark message as completed if status is COMPLETE
              if (data?.status === CHAT_STATUS?.COMPLETE && data?.id) {
                setCompletedMessageIds((prev) =>
                  new Set(prev).add(Number(data.id))
                );
              }
              // genai failed before producing any answer: say so instead of showing nothing
              if (
                data?.status === CHAT_STATUS?.EXCEPTION &&
                !parseChatRecord(data).answerText
              ) {
                showChatError(data?.id, t("doosanAI.responseFailed"));
              }
              // stop polling, clear chatId but keep threadId
              setIsMultiAIPolling(false);
              setMultiAIChatId(null);
              setCurrentAIResponseId(null);
              setCurrentStatus(null);
              // clear input for next query
              setState({ ...defaultState, doazGptInput: "" });
              clearInterval(pollingInterval);
            }
          },
          onError: (error: any) => {
            console.error("Multi-AI Chat polling error:", error);
            pollFailuresRef.current += 1;
            if (pollFailuresRef.current < MAX_POLL_FAILURES) return;
            // Give up: clear the in-flight chat so new questions aren't held back
            pollFailuresRef.current = 0;
            showChatError(multiAIChatId, t("doosanAI.responseFailed"));
            setIsMultiAIPolling(false);
            setMultiAIChatId(null);
            setCurrentAIResponseId(null);
            setCurrentStatus(null);
            clearInterval(pollingInterval);
          },
        };
        dispatch(doMultiAIChat(pollingPayload));
      }, POLLING_INTERVAL);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [
    isMultiAIPolling,
    multiAIChatId,
    multiAIThreadId,
    lastQuery,
    lastType,
    multiAIChatType,
    dispatch,
  ]);

  const onSubmitHandler = (
    inputName: any,
    selectedModelId?: number,
    fromQueue: boolean = false
  ) => {
    console.log("🚀 onSubmitHandler called with:", {
      inputName,
      selectedModelId,
      currentPathname,
    });
    const isMultiAIRoute = [
      ROUTE_NAMES.MULTI_AI_CHATBOT,
      ROUTE_NAMES.ASK_AI_CHATBOT,
      ROUTE_NAMES.LEGAL_COMPLIANCE,
    ].some((route) => pathname?.includes(route));

    // A reply is still generating (e.g. sources after follow-ups appear). Sending
    // now would carry its chatId, which the backend treats as a poll of the old
    // chat, so the question would be dropped. Show it and send it once done.
    if (isMultiAIRoute && isMultiAIPolling && multiAIChatId && !fromQueue) {
      const queued = inputName || state.doazGptInput;
      if (!queued?.trim()) return;
      setMultiAIMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: USER,
          content: queued,
          timestamp: new Date().toISOString(),
          modelType: selectedModelId || 1,
        },
      ]);
      setQueuedQuery({ input: queued, modelId: selectedModelId });
      setState({ ...defaultState, doazGptInput: "" });
      return;
    }

    // Validate first: setting the polling flag on empty input left the spinner stuck
    if (!validator(inputName) && filesPreview?.length === 0) {
      console.log("❌ validator failed");
      return;
    }
    setIsMultiAIPolling(true);
    setIsPaused(false);
    pollFailuresRef.current = 0;
    console.log("✅ isMultiAIPolling set to true");
    setCurrentMessageResponse?.(null);
    dispatch(updateThreadList([] as any));
    const input = inputName || state.doazGptInput;

    // Handle Multi-AI Chatbot and Ask-AI Chatbot using the same /api/chats flow
    if (isMultiAIRoute) {
      console.log("✅ Route matched! Processing Multi-AI Chat");

      // Use default model (id: 1) if no model is selected
      const modelId = selectedModelId || 1;

      // Determine if this is the first request or a subsequent one
      const isFirstRequest = !multiAIChatId || !multiAIThreadId;

      // Store the query and type for polling
      console.log("Storing query and type for polling");
      setLastQuery(input);
      setLastType(modelId);

      // Server will provide id; do not generate locally

      // Add user message to chat
      const userMessage = {
        id: Date.now(),
        type: USER,
        content: input,
        timestamp: new Date().toISOString(),
        modelType: modelId,
      };
      console.log("userMessage", userMessage);
      // Queued questions were already shown when they were asked
      if (!fromQueue) setMultiAIMessages((prev) => [...prev, userMessage]);
      console.log("multiAIMessages after adding user message", multiAIMessages);

      // Call the new multi-AI chat API
      // Rule: For very first-ever call → pass nothing.
      // If we have a threadId but no chatId (new chat after previous one), include only threadId on the first call.
      // If we have both (subsequent requests within the same chat), include both.
      const multiAIPayload = {
        query: input,
        type: modelId,
        chatType: multiAIChatType,
        ...(multiAIChatId
          ? { chatId: multiAIChatId, threadId: multiAIThreadId }
          : multiAIThreadId
          ? { threadId: multiAIThreadId }
          : {}),
        isPolling: false,
        onSuccess: (data: any) => {
          console.log("Multi-AI Chat response:", data);

          // Track the current status
          setCurrentStatus(data?.status);

          // Extract chatId and threadId from response for subsequent requests
          if (data?.id && data?.threadId) {
            setMultiAIChatId(data.id);
            setMultiAIThreadId(data.threadId);

            // Start polling if this is the first request
            if (isFirstRequest) {
              console.log("Starting polling");
              setIsMultiAIPolling(true);
            }
          }

          // AI response will be handled by polling when status is 4
          // Handle the response data here
          if (
            data?.status === CHAT_STATUS?.COMPLETE ||
            data?.status === CHAT_STATUS?.EXCEPTION ||
            data?.status === CHAT_STATUS?.NO_CHANGE
          ) {
            console.log("Multi-AI Chat status is complete or exception");
            // Mark message as completed if status is COMPLETE
            if (data?.status === CHAT_STATUS?.COMPLETE && data?.id) {
              setCompletedMessageIds((prev) =>
                new Set(prev).add(Number(data.id))
              );
            }
            // Stop polling, clear chatId for next query, keep threadId
            setIsMultiAIPolling(false);
            setMultiAIChatId(null);
            setCurrentStatus(null);
            setState({ ...defaultState, doazGptInput: "" });
          }
        },
        onError: (error: any) => {
          console.error("Multi-AI Chat error:", error);
          setIsMultiAIPolling(false);
          setCurrentStatus(null);
          showChatError(`send-error-${userMessage.id}`, t("doosanAI.sendFailed"));
        },
      };
      dispatch(doMultiAIChat(multiAIPayload));
      setState({ ...defaultState, doazGptInput: "" });
      setShowWelcomeText(false);
      return;
    }

    if (urlType) {
      setUrl(input);
      setQueryType(urlType);
    }
    const queryPayload = {
      payload: {
        pdf_id: documentData?.id,
        query: input,
        s3_file_path: getFilesPath(),
        doc_name: getDocNames(),
        source: getSource(),
        mode: getMode()?.toString(),
        project_id: getMode(),
        fileName: getFilename(),
        page_no: getPdfPageNum(),
        language:
          currentLanguage === "EN"
            ? PAYLOAD_LANGUAGE.ENGLISH
            : PAYLOAD_LANGUAGE.KOREAN,
        chat_history: [],
      },
      id: generateRandomId("chat"),
      onSuccess: () => {
        dispatch(
          getThreads({
            onSuccess: (res: any) => dispatch(updateThreads(res)),
          })
        );
        getInitialThreads();
      },
    };
    if (currentPathname?.includes(ROUTE_NAMES.ASK_AI_CHATBOT)) {
      navigate(ROUTE_NAMES.ASK_AI_CHATBOT_CHAT);
      dispatch(doQueryProject(queryPayload));
      if (docChatMode?.value === "project") {
        const project = projectsData?.rows?.find(
          (project: any) => project?.name === selectedProject
        );
        if (project) {
          handleProjectClick(project);
        }
      }
    } else if (currentPathname?.includes(ROUTE_NAMES.LEGAL_COMPLIANCE)) {
      // Legal Compliance stays on the same page - no navigation needed
      // The multi-AI chat API call is already handled above
      return;
    } else if (currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
      dispatch(doQueryDrawingAnalysis(queryPayload));
    } else if (currentPathname?.includes("document")) {
      navigate(ROUTE_NAMES.DOCUMENT_CHAT);
      dispatch(doQueryProject(queryPayload));
      if (docChatMode?.value === "project") {
        const project = projectsData?.rows?.find(
          (project: any) => project?.name === selectedProject
        );
        if (project) {
          handleProjectClick(project);
        }
      }
    } else if (currentPathname?.includes(DOCUMENT_TYPE.CHECKLIST)) {
      navigate(ROUTE_NAMES.CHECKLIST_CHAT);
      dispatch(doQueryMultiDoc(queryPayload));
    } else {
      navigate(ROUTE_NAMES.KSCE_CHAT_SCREEN);
      dispatch(doQuery(queryPayload));
    }
    setInput("");
    setShowWelcomeText(false);
  };

  const onCardClick = (q: string) => {
    onSubmitHandler(q);
  };

  const onModelChange = (option: any) => {
    dispatch(updateSelectedModel(option));
  };

  // The current conversation's thread id is kept per tab so a refresh can reload it
  const threadStorageKey = `multiAIThread:${multiAIChatType}`;

  useEffect(() => {
    if (multiAIThreadId) {
      sessionStorage.setItem(threadStorageKey, String(multiAIThreadId));
    }
  }, [multiAIThreadId, threadStorageKey]);

  // Reload the current conversation after a refresh
  const restoreMultiAIThread = async () => {
    // Stored as a string; the backend validates threadId as an integer
    const threadId = Number(sessionStorage.getItem(threadStorageKey));
    if (!threadId || multiAIMessages.length) return;
    await loadMultiAIThread(threadId);
  };

  // Open a past conversation from the history list
  const openMultiAIThread = async (threadId: number) => {
    if (Number(multiAIThreadId) === threadId) return;
    resetMultiAIChat();
    sessionStorage.setItem(threadStorageKey, String(threadId));
    await loadMultiAIThread(threadId);
  };

  const loadMultiAIThread = async (threadId: number) => {
    const { data, error } = await new ChatService().getChatsByThread(
      baseUrl,
      String(threadId)
    );
    const chats = [...(data?.rows ?? [])].reverse(); // API returns newest first
    if (error || !chats.length) {
      sessionStorage.removeItem(threadStorageKey);
      return;
    }

    const messages: any[] = [];
    const completed = new Set<number>();
    let lastSources: any[] = [];
    chats.forEach((chat: any) => {
      messages.push({
        id: `q-${chat.id}`,
        type: USER,
        content: chat.query,
        timestamp: chat.createdAt,
        modelType: chat.type,
        skipTypewriter: true,
      });
      const { answerText, parsedSources, parsedFollowup } =
        parseChatRecord(chat);
      if (answerText) {
        messages.push({
          id: chat.id,
          type: AI,
          content: answerText,
          timestamp: chat.updatedAt,
          sources: parsedSources,
          followUp: parsedFollowup,
          skipTypewriter: true,
        });
        if (parsedSources.length) lastSources = parsedSources;
      } else if (chat.status === CHAT_STATUS?.EXCEPTION) {
        messages.push({
          id: chat.id,
          type: AI,
          content: t("doosanAI.responseFailed"),
          timestamp: chat.updatedAt,
          isError: true,
          skipTypewriter: true,
        });
      }
      if (chat.status === CHAT_STATUS?.COMPLETE) completed.add(Number(chat.id));
    });

    setMultiAIMessages(messages);
    setMultiAISources(lastSources);
    setCompletedMessageIds(completed);
    setMultiAIThreadId(threadId as any);
    setShowWelcomeText(false);

    // Resume polling if the last reply was still being generated
    const last = chats[chats.length - 1];
    if (!FINISHED_CHAT_STATUSES.includes(last.status)) {
      setLastQuery(last.query);
      setLastType(last.type);
      setMultiAIChatId(last.id);
      setIsMultiAIPolling(true);
    }
  };

  const resetMultiAIChat = () => {
    console.log("resetMultiAIChat called");
    sessionStorage.removeItem(threadStorageKey);
    setMultiAIChatId(null);
    setMultiAIThreadId(null);
    setIsMultiAIPolling(false);
    setLastQuery("");
    setLastType(0);
    setMultiAIMessages([]);
    setMultiAISources([]);
    setCurrentAIResponseId(null);
    setIsPaused(false);
    setCurrentStatus(null);
    setCompletedMessageIds(new Set());
  };

  const stopPolling = async () => {
    console.log(
      "stopPolling called - stopping polling and clearing chat states"
    );

    const chatId = multiAIChatId;
    if (!chatId) return;

    // End the reply in the UI right away. The backend only cancels while text is
    // streaming (not while sources are generating), so don't wait on its answer.
    setIsMultiAIPolling(false);
    setMultiAIChatId(null);
    setCurrentAIResponseId(null);
    setCurrentStatus(null);
    setQueuedQuery(null);
    setCompletedMessageIds((prev) => new Set(prev).add(Number(chatId)));

    try {
      const chatService = new ChatService();
      await chatService.stopChat(baseUrl, chatId);
    } catch (error) {
      console.error("Error calling stop chat API:", error);
    }
  };

  // Send a queued question as soon as the in-flight reply has finished
  useEffect(() => {
    if (queuedQuery && !multiAIChatId) {
      const { input, modelId } = queuedQuery;
      setQueuedQuery(null);
      onSubmitHandler(input, modelId, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedQuery, multiAIChatId]);

  return {
    t,
    state,
    inputChangeHandler,
    selectedField,
    setSelectedField,
    selectedFiles,
    inputRef,
    conGptPopover,
    doazGptInputRef,
    conGptInputRef,
    onSubmitHandler,
    riskCheckerQuestions,
    conGptQuestions,
    doazGptQuestions,
    questions,
    onCardClick,
    onModelChange,
    modelOptions,
    resetMultiAIChat,
    restoreMultiAIThread,
    openMultiAIThread,
    multiAIThreadId,
    multiAIChatType,
    multiAIMessages,
    multiAISources,
    isMultiAIPolling,
    stopPolling,
    isPaused,
    currentStatus,
    completedMessageIds,
  };
};

export default useProjectsDashboard;
